/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, useEffect } from "react";

import { IconButton } from "@mui/material";
import { Map as MapIcon } from "@mui/icons-material";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../context/StateContext";
import { Location, DifferentLocation } from "../../../models/location";
import AutoSuggest, { AutoSuggestProps } from "../AutoSuggest";
import BoxForm from "../Box";
import useStyles from "./styles";

interface Props extends AutoSuggestProps<google.maps.places.QueryAutocompletePrediction> {
  onChange?: (location: DifferentLocation | Location) => void;
  mapIsOpen?: boolean;
  location?: DifferentLocation | Location;
  showMapIcon?: boolean;
  disabledPlaceholder?: string;
}

const DEFAULT_LATITUDE = -25.274398;
const DEFAULT_LONGITUDE = 133.77513599999997;

const MapInput = (props: Props) => {
  const { mapIsLoaded } = useAppState();
  const { t: translate } = useTranslation();

  const {
    onChange,
    location,
    showMapIcon,
    mapIsOpen,
    disabled,
    disabledPlaceholder,
    placeholder,
    ...rest
  } = props;

  const classes = useStyles();

  const [_mapIsOpen, setMapIsOpen] = useState(false);

  const [_location, setLocation] = useState<DifferentLocation | Location>();

  useEffect(() => {
    setLocation(location);
  }, [location]);

  useEffect(() => {
    if (mapIsOpen !== undefined) setMapIsOpen(mapIsOpen);
  }, [mapIsOpen]);

  const onMapLoad = () => {
    navigator?.geolocation.getCurrentPosition(({ coords: { latitude, longitude } }) => {
      if (onChange)
        onChange({
          ...(location || {}),
          latitude: Number(_location?.latitude) || latitude,
          longitude: Number(_location?.longitude) || longitude,
        });

      setLocation({
        ...(location || {}),
        latitude: Number(_location?.latitude) || latitude,
        longitude: Number(_location?.longitude) || longitude,
      });
    });
  };

  const onPlacesChanged = (place: google.maps.places.QueryAutocompletePrediction) => {
    const element = document.getElementById("placeAttr")?.children[0];

    if (place && place?.place_id && element && mapIsLoaded) {
      const service = new google.maps.places.PlacesService(
        new google.maps.Map(element as HTMLElement)
      );

      service.getDetails(
        {
          placeId: place.place_id,
          fields: ["address_components", "formatted_address", "geometry"],
        },
        (placeSelected) => {
          const locationUpdated: DifferentLocation = {};

          if (
            placeSelected &&
            placeSelected?.address_components &&
            placeSelected?.address_components?.length > 0
          ) {
            locationUpdated.number =
              placeSelected.address_components.find((a) =>
                a.types.some((t) => t.includes("street_number"))
              )?.long_name ||
              _location?.number ||
              "";
            locationUpdated.addressLine1 =
              placeSelected.address_components.find((a) => a.types.some((t) => t.includes("route")))
                ?.long_name ||
              placeSelected.address_components.find((a) =>
                a.types.some((t) => t.includes("sublocality"))
              )?.long_name ||
              _location?.addressLine1 ||
              "";

            locationUpdated.suburb =
              placeSelected.address_components.find((a) =>
                a.types.some((t) => t === "locality" || t.includes("administrative_area_level_2"))
              )?.short_name ||
              _location?.suburb ||
              "";
            locationUpdated.country =
              placeSelected.address_components.find((a) =>
                a.types.some((t) => t.includes("country"))
              )?.long_name ||
              _location?.country ||
              "";
            locationUpdated.postalcode =
              placeSelected.address_components.find((a) =>
                a.types.some((t) => t.includes("postal_code"))
              )?.long_name ||
              _location?.postalcode ||
              "";

            locationUpdated.latitude =
              placeSelected?.geometry?.location?.lat() || _location?.latitude;
            locationUpdated.longitude =
              placeSelected?.geometry?.location?.lng() || _location?.longitude;
            locationUpdated.state =
              placeSelected.address_components.find((a) =>
                a.types.some((t) => t.includes("administrative_area_level_1"))
              )?.long_name ||
              _location?.state ||
              "";
            locationUpdated.longAddress =
              placeSelected?.formatted_address || _location?.longAddress || "";
          }
          if (onChange) {
            onChange({
              ...(_location || {}),
              ...locationUpdated,
            });
          }

          setLocation({
            ...(_location || {}),
            ...locationUpdated,
          });
        }
      );
    }
  };

  const onSearch = async (value: string): Promise<AxiosResponse<any[], any>> => {
    const service = new google.maps.places.AutocompleteService();

    return new Promise((resolve, reject) => {
      service.getQueryPredictions(
        {
          input: value,
        },
        (predictions, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.INVALID_REQUEST ||
            status === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT ||
            status === google.maps.places.PlacesServiceStatus.REQUEST_DENIED ||
            status === google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR
          ) {
            reject(status);
          } else {
            resolve({ data: predictions, status: 200 } as AxiosResponse);
          }
        }
      );
    });
  };

  const renderRightIcon = () => {
    if (showMapIcon !== false) {
      return (
        <IconButton className={classes.iconButton} onClick={() => setMapIsOpen(!_mapIsOpen)}>
          <MapIcon htmlColor={_mapIsOpen ? "#308CDB" : "#465869"} className="align-self-center" />
        </IconButton>
      );
    }

    return undefined;
  };

  const renderMap = () => {
    if (_mapIsOpen) {
      return (
        <BoxForm customClasses={classes.box}>
          <GoogleMap
            center={{
              lat: Number(_location?.latitude) || DEFAULT_LATITUDE,
              lng: Number(_location?.longitude) || DEFAULT_LONGITUDE,
            }}
            zoom={15}
            options={{
              minZoom: 2,
              maxZoom: 15,
            }}
            onLoad={() => onMapLoad()}
            mapContainerClassName={classes.map}
          >
            <Marker
              position={{
                lat: Number(_location?.latitude) || DEFAULT_LATITUDE,
                lng: Number(_location?.longitude) || DEFAULT_LONGITUDE,
              }}
            />
          </GoogleMap>
        </BoxForm>
      );
    }

    return null;
  };

  const renderInput = () => {
    if (mapIsLoaded) {
      return (
        <>
          <AutoSuggest
            {...rest}
            inputValue={_location?.longAddress ?? ""}
            onChangeInput={(value) => {
              const next = { ...(_location || {}), longAddress: value };
              setLocation(next);
              onChange?.(next);
            }}
            noOptionsText={translate("searchForLocation")}
            disableClearable
            rightIcon={renderRightIcon()}
            getOptionLabel={(place?: google.maps.places.QueryAutocompletePrediction) =>
              place?.description ?? ""
            }
            isOptionEqualToValue={(option, value) => option?.place_id === value?.place_id}
            onChange={mapIsLoaded ? onPlacesChanged : undefined}
            onSearch={mapIsLoaded ? onSearch : undefined}
            filterOptions={(options) => options}
            placeholder={disabled ? disabledPlaceholder : placeholder}
            disabled={disabled}
          />
          {renderMap()}
        </>
      );
    }

    return (
      <AutoSuggest
        {...rest}
        noOptionsText={translate("searchForLocation")}
        disableClearable
        rightIcon={renderRightIcon()}
        getOptionLabel={(place?: google.maps.places.QueryAutocompletePrediction) =>
          place && place?.description ? place?.description : ""
        }
        getOptionSelected={(
          option: google.maps.places.QueryAutocompletePrediction,
          value: google.maps.places.QueryAutocompletePrediction
        ) => option?.place_id === value?.place_id}
        onChange={mapIsLoaded ? onPlacesChanged : undefined}
        onSearch={mapIsLoaded ? onSearch : undefined}
        filterOptions={(options) => options}
      />
    );
  };

  return (
    <div id="placeAttr">
      <div></div>
      {renderInput()}
    </div>
  );
};

export default MapInput;
